import type {
  StripeCatalogResponse,
  StripeCheckoutSessionResponse,
  StripeCheckoutSessionResult,
  StripeCheckoutStatusResponse,
  StripeEntitlementResponse,
  StripeErrorResponse,
  StripePortalSessionResponse,
} from "store/billing/types"
import { getPlatformHeaders } from "./platformApiAuth"
import {
  isBillingPaywallError,
  syncBillingDenyState,
  syncBillingEntitlement,
} from "./platformBillingState"
import { getPlatformServiceEndpoint } from "./platformServiceEndpoints"
import { z } from "zod"

const nonEmptyString = z.string().trim().min(1)
const stripeEntitlementSchema = z
  .object({
    accessState: z.enum(["inactive", "entitled", "portal_required", "locked", "admin_bypass"]),
    cancelAtPeriodEnd: z.boolean(),
    customerId: nonEmptyString.optional(),
    entitled: z.boolean(),
    entitlementStatus: nonEmptyString,
    lockReason: z
      .enum([
        "no_subscription",
        "subscription_canceled",
        "trial_canceled",
        "payment_required",
        "payment_overdue",
        "subscription_paused",
      ])
      .optional(),
    onboardingFlowVariant: z.enum(["control", "test"]).optional(),
    onboardingTrialEndsAt: z.number().finite().optional(),
    onboardingTrialStartedAt: z.number().finite().optional(),
    onboardingTrialStatus: z.enum(["not_started", "active", "expired"]).optional(),
    requiresPortal: z.boolean(),
    subscriptionId: nonEmptyString.optional(),
  })
  .passthrough()

const stripeCatalogSchema = z
  .object({
    offers: z.array(
      z
        .object({
          amount: z.number().int().nonnegative(),
          currency: nonEmptyString,
          description: z.string(),
          features: z.array(z.string()),
          highlight: z.boolean().optional(),
          interval: z.enum(["day", "week", "month", "year"]),
          intervalCount: z.number().int().positive(),
          name: nonEmptyString,
          priceId: nonEmptyString,
          productId: nonEmptyString,
        })
        .passthrough()
    ),
    trialEligible: z.boolean(),
  })
  .passthrough()

const stripeCheckoutSessionSchema = z
  .object({
    sessionId: nonEmptyString,
    url: z.string().url(),
  })
  .passthrough()

const stripePortalSessionSchema = z.object({ url: z.string().url() }).passthrough()

const stripeCheckoutStatusSchema = z
  .object({
    entitlement: stripeEntitlementSchema,
    expiresAt: z.number().finite().optional(),
    sessionId: nonEmptyString,
    status: z.enum(["open", "processing", "completed", "failed", "expired"]),
    url: z.string().url().optional(),
  })
  .passthrough()

export const parseStripeEntitlementResponse = (value: unknown): StripeEntitlementResponse =>
  stripeEntitlementSchema.parse(value) as StripeEntitlementResponse

export const parseStripeCatalogResponse = (value: unknown): StripeCatalogResponse =>
  stripeCatalogSchema.parse(value) as StripeCatalogResponse

const parseStripeCheckoutSessionResponse = (value: unknown): StripeCheckoutSessionResponse =>
  stripeCheckoutSessionSchema.parse(value) as StripeCheckoutSessionResponse

const parseStripePortalSessionResponse = (value: unknown): StripePortalSessionResponse =>
  stripePortalSessionSchema.parse(value) as StripePortalSessionResponse

const parseStripeCheckoutStatusResponse = (value: unknown): StripeCheckoutStatusResponse =>
  stripeCheckoutStatusSchema.parse(value) as StripeCheckoutStatusResponse

class BillingRequestError extends Error {
  data: StripeErrorResponse | null
  status: number

  constructor(message: string, status: number, data: StripeErrorResponse | null) {
    super(message)
    this.name = "BillingRequestError"
    this.data = data
    this.status = status
  }
}

export class BillingApi {
  private get billingEndpoint() {
    return getPlatformServiceEndpoint("billing")
  }

  private async parseBillingError(response: Response): Promise<StripeErrorResponse | null> {
    try {
      return (await response.json()) as StripeErrorResponse
    } catch {
      return null
    }
  }

  private async requestBillingJson<T>(
    path: string,
    options: {
      body?: string
      contentType?: string
      defaultErrorMessage: string
      method: "GET" | "POST"
      parse: (value: unknown) => T
      suppressPaywall?: boolean
    }
  ): Promise<T> {
    const response = await fetch(`${this.billingEndpoint}${path}`, {
      method: options.method,
      headers: await getPlatformHeaders(options.contentType),
      ...(options.body ? { body: options.body } : {}),
    })

    if (!response.ok) {
      const data = await this.parseBillingError(response)

      // Keep the real status + backend code/denyCode in the console — the
      // thrown error only carries the user-facing message, and masking the
      // diagnosis is what made the 2026-07 403 storm undebuggable from the FE.
      console.error(`[billingApi] ${options.method} ${path} -> ${response.status}`, data)

      if (!options.suppressPaywall && isBillingPaywallError(data)) syncBillingDenyState(data!)

      throw new BillingRequestError(options.defaultErrorMessage, response.status, data)
    }

    try {
      return options.parse(await response.json())
    } catch {
      throw new BillingRequestError(options.defaultErrorMessage, 502, null)
    }
  }

  async stripeEntitlement(options?: {
    suppressPaywall?: boolean
  }): Promise<StripeEntitlementResponse> {
    const entitlement = await this.requestBillingJson<StripeEntitlementResponse>(
      "/customer/entitlement",
      {
        defaultErrorMessage: "We couldn't check your access. Please try again.",
        method: "GET",
        parse: parseStripeEntitlementResponse,
        suppressPaywall: options?.suppressPaywall,
      }
    )

    syncBillingEntitlement(entitlement)
    return entitlement
  }

  async markOnboardingActivation(options?: {
    suppressPaywall?: boolean
  }): Promise<StripeEntitlementResponse> {
    const entitlement = await this.requestBillingJson<StripeEntitlementResponse>(
      "/customer/onboarding-activation",
      {
        defaultErrorMessage: "We couldn't activate your access. Please try again.",
        method: "POST",
        parse: parseStripeEntitlementResponse,
        suppressPaywall: options?.suppressPaywall,
      }
    )

    syncBillingEntitlement(entitlement)
    return entitlement
  }

  async stripeCatalog(options?: { suppressPaywall?: boolean }): Promise<StripeCatalogResponse> {
    return this.requestBillingJson<StripeCatalogResponse>("/catalog", {
      defaultErrorMessage: "We couldn't load the plans. Please try again.",
      method: "GET",
      parse: parseStripeCatalogResponse,
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async stripeCheckoutSession(
    priceId: string,
    options: {
      operationId: string
      returnPath?: string
      suppressPaywall?: boolean
    }
  ): Promise<StripeCheckoutSessionResult> {
    const response = await fetch(`${this.billingEndpoint}/checkout-session`, {
      method: "POST",
      headers: await getPlatformHeaders("application/json"),
      body: JSON.stringify({
        operationId: options.operationId,
        priceId,
        returnPath: options.returnPath,
      }),
    })

    if (response.ok)
      try {
        return {
          ok: true,
          data: parseStripeCheckoutSessionResponse(await response.json()),
        }
      } catch {
        throw new BillingRequestError(
          "We couldn't open the payment page. Please try again.",
          502,
          null
        )
      }

    const data = await this.parseBillingError(response)
    if (!options.suppressPaywall && isBillingPaywallError(data)) syncBillingDenyState(data!)

    return {
      ok: false,
      status: response.status,
      data,
    }
  }

  async stripePortalSession(options?: {
    returnPath?: string
    suppressPaywall?: boolean
  }): Promise<StripePortalSessionResponse> {
    return this.requestBillingJson<StripePortalSessionResponse>("/portal-session", {
      body: JSON.stringify({
        returnPath: options?.returnPath,
      }),
      contentType: "application/json",
      defaultErrorMessage: "We couldn't open your plan page. Please try again.",
      method: "POST",
      parse: parseStripePortalSessionResponse,
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async stripeCheckoutStatus(
    sessionId: string,
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<StripeCheckoutStatusResponse> {
    const query = new URLSearchParams({
      sessionId,
    })
    const status = await this.requestBillingJson<StripeCheckoutStatusResponse>(
      `/customer/checkout-status?${query.toString()}`,
      {
        defaultErrorMessage: "We couldn't check your payment yet. Please try again.",
        method: "GET",
        parse: parseStripeCheckoutStatusResponse,
        suppressPaywall: options?.suppressPaywall,
      }
    )

    syncBillingEntitlement(status.entitlement)
    return status
  }

  // Redirect-time finalize: asks the backend to retrieve the Stripe checkout
  // session and, if paid, write the entitlement immediately (via the shared
  // webhook pipeline) instead of waiting on webhook delivery. Returns the same
  // shape as stripeCheckoutStatus.
  async stripeCheckoutFinalize(
    sessionId: string,
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<StripeCheckoutStatusResponse> {
    const status = await this.requestBillingJson<StripeCheckoutStatusResponse>(
      "/customer/checkout-finalize",
      {
        body: JSON.stringify({ sessionId }),
        contentType: "application/json",
        defaultErrorMessage: "We couldn't confirm your payment yet. Please try again.",
        method: "POST",
        parse: parseStripeCheckoutStatusResponse,
        suppressPaywall: options?.suppressPaywall,
      }
    )

    syncBillingEntitlement(status.entitlement)
    return status
  }
}

export const billingApi = new BillingApi()
